import { useState, useCallback, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  QuestionResult,
  type QuestionAnswer,
  type TeacherOverride,
  type GradingStatus,
} from "./QuestionResult";
import {
  ClipboardCheck,
  User,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Eye,
  Filter,
  ChevronDown,
  ChevronUp,
  Flag,
  Save,
  RotateCcw,
} from "lucide-react";

// ============================================================================
// Types
// ============================================================================

export interface GradingResultsData {
  /** Unique ID for this grading session */
  sessionId: string;
  /** Worksheet information */
  worksheetId: string;
  worksheetTitle: string;
  /** Student information (extracted from worksheet) */
  studentName: string | null;
  studentSheetNumber: string | null;
  /** Grading timestamp */
  gradedAt: string;
  /** Individual question answers */
  answers: QuestionAnswer[];
  /** Summary statistics */
  totalPoints: number;
  maxPoints: number;
  autoGradedCount: number;
  needsReviewCount: number;
}

export interface GradingResultsProps {
  /** Grading data to display */
  data: GradingResultsData;
  /** Teacher overrides (keyed by questionId) */
  overrides?: Record<string, TeacherOverride>;
  /** Called when teacher changes an override */
  onOverridesChange?: (overrides: Record<string, TeacherOverride>) => void;
  /** Called when teacher saves all changes */
  onSave?: (overrides: Record<string, TeacherOverride>) => void;
  /** Whether save is in progress */
  isSaving?: boolean;
  /** Additional class name */
  className?: string;
}

type FilterMode = "all" | "correct" | "incorrect" | "needs_review" | "flagged";

// ============================================================================
// Helper Functions
// ============================================================================

function calculateScoreWithOverrides(
  answers: QuestionAnswer[],
  overrides: Record<string, TeacherOverride>
): { totalPoints: number; maxPoints: number; percentage: number } {
  let totalPoints = 0;
  let maxPoints = 0;

  for (const answer of answers) {
    maxPoints += answer.maxPoints;

    const override = overrides[answer.questionId];
    const effectiveStatus = override?.overrideStatus ?? answer.autoGradingResult;

    if (effectiveStatus === "correct") {
      totalPoints += answer.maxPoints;
    } else if (effectiveStatus === "needs_review") {
      // For needs_review, use the original auto-grade points (partial credit)
      totalPoints += answer.points;
    }
    // incorrect and skipped = 0 points
  }

  const percentage = maxPoints > 0 ? Math.round((totalPoints / maxPoints) * 100) : 0;

  return { totalPoints, maxPoints, percentage };
}

function getScoreColor(percentage: number): string {
  if (percentage >= 80) return "text-green-600";
  if (percentage >= 60) return "text-amber-600";
  return "text-red-600";
}

function getProgressColor(percentage: number): string {
  if (percentage >= 80) return "bg-green-500";
  if (percentage >= 60) return "bg-amber-500";
  return "bg-red-500";
}

// ============================================================================
// Sub-Components
// ============================================================================

interface ScoreSummaryProps {
  totalPoints: number;
  maxPoints: number;
  percentage: number;
  hasChanges: boolean;
}

function ScoreSummary({
  totalPoints,
  maxPoints,
  percentage,
  hasChanges,
}: ScoreSummaryProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">Score</span>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Badge variant="outline" className="text-xs">
              Unsaved changes
            </Badge>
          )}
          <span className={cn("text-2xl sm:text-3xl font-bold", getScoreColor(percentage))}>
            {totalPoints}/{maxPoints}
          </span>
        </div>
      </div>

      <div className="space-y-1">
        <Progress
          value={percentage}
          className="h-3"
          // Custom color based on score
          style={
            {
              "--progress-indicator-color": percentage >= 80
                ? "rgb(34 197 94)"
                : percentage >= 60
                ? "rgb(245 158 11)"
                : "rgb(239 68 68)",
            } as React.CSSProperties
          }
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>0%</span>
          <span className={cn("font-medium", getScoreColor(percentage))}>
            {percentage}%
          </span>
          <span>100%</span>
        </div>
      </div>
    </div>
  );
}

interface QuickStatsProps {
  correctCount: number;
  incorrectCount: number;
  needsReviewCount: number;
  flaggedCount: number;
}

function QuickStats({
  correctCount,
  incorrectCount,
  needsReviewCount,
  flaggedCount,
}: QuickStatsProps) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
      <div className="flex items-center gap-2 p-2 sm:p-3 rounded-lg bg-green-50 border border-green-100">
        <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
        <div>
          <div className="text-lg sm:text-xl font-bold text-green-700">{correctCount}</div>
          <div className="text-xs text-green-600">Correct</div>
        </div>
      </div>

      <div className="flex items-center gap-2 p-2 sm:p-3 rounded-lg bg-red-50 border border-red-100">
        <XCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 flex-shrink-0" />
        <div>
          <div className="text-lg sm:text-xl font-bold text-red-700">{incorrectCount}</div>
          <div className="text-xs text-red-600">Incorrect</div>
        </div>
      </div>

      <div className="flex items-center gap-2 p-2 sm:p-3 rounded-lg bg-amber-50 border border-amber-100">
        <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 flex-shrink-0" />
        <div>
          <div className="text-lg sm:text-xl font-bold text-amber-700">{needsReviewCount}</div>
          <div className="text-xs text-amber-600">Review</div>
        </div>
      </div>

      <div className="flex items-center gap-2 p-2 sm:p-3 rounded-lg bg-purple-50 border border-purple-100">
        <Flag className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 flex-shrink-0" />
        <div>
          <div className="text-lg sm:text-xl font-bold text-purple-700">{flaggedCount}</div>
          <div className="text-xs text-purple-600">Flagged</div>
        </div>
      </div>
    </div>
  );
}

interface FilterBarProps {
  currentFilter: FilterMode;
  onFilterChange: (filter: FilterMode) => void;
  counts: {
    all: number;
    correct: number;
    incorrect: number;
    needs_review: number;
    flagged: number;
  };
}

function FilterBar({ currentFilter, onFilterChange, counts }: FilterBarProps) {
  const filters: { value: FilterMode; label: string; count: number }[] = [
    { value: "all", label: "All", count: counts.all },
    { value: "correct", label: "Correct", count: counts.correct },
    { value: "incorrect", label: "Incorrect", count: counts.incorrect },
    { value: "needs_review", label: "Review", count: counts.needs_review },
    { value: "flagged", label: "Flagged", count: counts.flagged },
  ];

  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
      <Filter className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      {filters.map((filter) => (
        <Button
          key={filter.value}
          variant={currentFilter === filter.value ? "default" : "outline"}
          size="sm"
          onClick={() => onFilterChange(filter.value)}
          className="flex-shrink-0"
          aria-pressed={currentFilter === filter.value}
        >
          {filter.label}
          <Badge
            variant="secondary"
            className={cn(
              "ml-1.5 text-xs",
              currentFilter === filter.value && "bg-primary-foreground/20"
            )}
          >
            {filter.count}
          </Badge>
        </Button>
      ))}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function GradingResults({
  data,
  overrides = {},
  onOverridesChange,
  onSave,
  isSaving = false,
  className,
}: GradingResultsProps) {
  // Local state
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
  const [isAllExpanded, setIsAllExpanded] = useState(false);

  // Calculate stats with overrides
  const stats = useMemo(() => {
    let correctCount = 0;
    let incorrectCount = 0;
    let needsReviewCount = 0;
    let flaggedCount = 0;

    for (const answer of data.answers) {
      const override = overrides[answer.questionId];
      const effectiveStatus = override?.overrideStatus ?? answer.autoGradingResult;

      switch (effectiveStatus) {
        case "correct":
          correctCount++;
          break;
        case "incorrect":
          incorrectCount++;
          break;
        case "needs_review":
          needsReviewCount++;
          break;
      }

      if (override?.isFlagged) {
        flaggedCount++;
      }
    }

    return {
      correctCount,
      incorrectCount,
      needsReviewCount,
      flaggedCount,
    };
  }, [data.answers, overrides]);

  // Calculate score with overrides
  const { totalPoints, maxPoints, percentage } = useMemo(
    () => calculateScoreWithOverrides(data.answers, overrides),
    [data.answers, overrides]
  );

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    return Object.keys(overrides).some((questionId) => {
      const override = overrides[questionId];
      return (
        override.overrideStatus !== null ||
        override.teacherNote !== "" ||
        override.isFlagged
      );
    });
  }, [overrides]);

  // Filter counts
  const filterCounts = useMemo(
    () => ({
      all: data.answers.length,
      correct: stats.correctCount,
      incorrect: stats.incorrectCount,
      needs_review: stats.needsReviewCount,
      flagged: stats.flaggedCount,
    }),
    [data.answers.length, stats]
  );

  // Filtered answers
  const filteredAnswers = useMemo(() => {
    return data.answers.filter((answer) => {
      const override = overrides[answer.questionId];
      const effectiveStatus = override?.overrideStatus ?? answer.autoGradingResult;

      switch (filterMode) {
        case "correct":
          return effectiveStatus === "correct";
        case "incorrect":
          return effectiveStatus === "incorrect";
        case "needs_review":
          return effectiveStatus === "needs_review";
        case "flagged":
          return override?.isFlagged === true;
        default:
          return true;
      }
    });
  }, [data.answers, overrides, filterMode]);

  // Handlers
  const handleOverrideChange = useCallback(
    (override: TeacherOverride) => {
      if (onOverridesChange) {
        onOverridesChange({
          ...overrides,
          [override.questionId]: override,
        });
      }
    },
    [overrides, onOverridesChange]
  );

  const handleExpandToggle = useCallback((questionId: string) => {
    setExpandedQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });
  }, []);

  const handleExpandAll = useCallback(() => {
    if (isAllExpanded) {
      setExpandedQuestions(new Set());
    } else {
      setExpandedQuestions(new Set(data.answers.map((a) => a.questionId)));
    }
    setIsAllExpanded(!isAllExpanded);
  }, [isAllExpanded, data.answers]);

  const handleResetOverrides = useCallback(() => {
    if (onOverridesChange) {
      onOverridesChange({});
    }
  }, [onOverridesChange]);

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(overrides);
    }
  }, [onSave, overrides]);

  return (
    <div className={cn("space-y-4", className)}>
      {/* Header Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg sm:text-xl">Grading Results</CardTitle>
            </div>
            <Badge variant="secondary" className="self-start">
              {data.worksheetTitle}
            </Badge>
          </div>
          <CardDescription>
            {data.gradedAt && `Graded ${new Date(data.gradedAt).toLocaleDateString()}`}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Student info */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
              <User className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="font-medium text-base sm:text-lg">
                {data.studentName ?? "Unknown Student"}
              </div>
              {data.studentSheetNumber && (
                <div className="text-sm text-muted-foreground">
                  Sheet #{data.studentSheetNumber}
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Score summary */}
          <ScoreSummary
            totalPoints={totalPoints}
            maxPoints={maxPoints}
            percentage={percentage}
            hasChanges={hasChanges}
          />

          <Separator />

          {/* Quick stats */}
          <QuickStats
            correctCount={stats.correctCount}
            incorrectCount={stats.incorrectCount}
            needsReviewCount={stats.needsReviewCount}
            flaggedCount={stats.flaggedCount}
          />
        </CardContent>
      </Card>

      {/* Questions Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="text-base sm:text-lg">Questions</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExpandAll}
                aria-label={isAllExpanded ? "Collapse all" : "Expand all"}
              >
                {isAllExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-1" />
                    Collapse All
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Expand All
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Filter bar */}
          <FilterBar
            currentFilter={filterMode}
            onFilterChange={setFilterMode}
            counts={filterCounts}
          />

          {/* Question list */}
          <div className="space-y-3">
            {filteredAnswers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No questions match the current filter.</p>
              </div>
            ) : (
              filteredAnswers.map((answer) => (
                <QuestionResult
                  key={answer.questionId}
                  answer={answer}
                  override={overrides[answer.questionId]}
                  onOverrideChange={onOverridesChange ? handleOverrideChange : undefined}
                  isExpanded={expandedQuestions.has(answer.questionId)}
                  onExpandToggle={() => handleExpandToggle(answer.questionId)}
                />
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Action Bar (fixed at bottom on mobile) */}
      {onOverridesChange && (
        <div className="sticky bottom-0 left-0 right-0 p-4 bg-background/95 backdrop-blur border-t shadow-lg sm:relative sm:p-0 sm:bg-transparent sm:border-0 sm:shadow-none">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:justify-end">
            {hasChanges && (
              <Button
                variant="outline"
                onClick={handleResetOverrides}
                disabled={isSaving}
                className="order-2 sm:order-1"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset Changes
              </Button>
            )}
            {onSave && (
              <Button
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
                className="order-1 sm:order-2"
              >
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
